import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, X, WifiOff } from "lucide-react";

export default function PWAManager() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [offline, setOffline] = useState(() => (typeof navigator !== "undefined" ? !navigator.onLine : false));
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("pwa-install-dismissed") === "1"; } catch { return false; }
  });

  // Online/offline detection
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => {
      setOffline(false);
      toast.success("Back online");
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  // Capture the browser's install prompt
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      toast.success("InsureHub installed");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Show a toast when a new version of the app is ready
  useEffect(() => {
    const onUpdate = (e) => {
      const reg = e.detail;
      toast("New version available", {
        description: "Refresh to get the latest InsureHub.",
        duration: Infinity,
        action: {
          label: "Refresh",
          onClick: () => {
            if (reg && reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
            else window.location.reload();
          },
        },
      });
    };
    window.addEventListener("sw-update-available", onUpdate);
    return () => window.removeEventListener("sw-update-available", onUpdate);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* ignore */ }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("pwa-install-dismissed", "1"); } catch { /* ignore */ }
  };

  const showInstall = deferredPrompt && !dismissed;

  return (
    <>
      {offline && (
        <div
          className="fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-2 bg-stone-800 text-white text-xs sm:text-sm font-medium py-2 px-4 shadow-md animate-in fade-in slide-in-from-top-2"
          data-testid="offline-banner"
          role="status"
        >
          <WifiOff className="w-4 h-4 shrink-0" />
          You're offline — showing saved content. Live data will refresh once you're reconnected.
        </div>
      )}

      {showInstall && (
        <div
          className="fixed bottom-20 right-4 z-[9998] flex items-center gap-2 rounded-full bg-[#E05A47] text-white shadow-lg shadow-[#E05A47]/30 pl-4 pr-2 py-2 animate-in fade-in slide-in-from-bottom-3"
          data-testid="pwa-install-banner"
        >
          <button onClick={handleInstall} className="flex items-center gap-2 text-sm font-medium" data-testid="pwa-install-btn">
            <Download className="w-4 h-4" />
            Install InsureHub
          </button>
          <button onClick={handleDismiss} aria-label="Dismiss" className="p-1 rounded-full hover:bg-white/15 transition-colors" data-testid="pwa-install-dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </>
  );
}
